import { useMemo, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { Button, LoadingButton } from "ui/button";
import { DialogActions, DialogBackButton } from "ui/dialog";
import { TouchpointTemplateItem } from "components/modals/create-campaign-from-template-modal/components/touchpoint-template-item";

import { useAddTouchpointsFromMondayTemplateContext } from "components/modals/add-touchpoints-from-template-modal/context/add-touchpoints-from-monday-template-context";

import { MondayTouchpointTemplateDTO } from "generated/schemas";

import Icons from "assets";

const TEMPLATE_LIST_SX: SxProps<Theme> = { display: "flex", flexDirection: "column", gap: 2 };

const TouchpointSelectionStep = () => {
  const {
    campaignTemplates,
    invalidTemplateIds,
    existingTouchpointNames,
    campaignObjective,
    isDirectToStep2,
    isLoadingCampaignTemplates,
  } = useAddTouchpointsFromMondayTemplateContext();
  const { watch } = useFormContext();

  const watchedTemplateValues = watch("campaignTemplates");

  const campaignObjectiveDescription = useMemo(() => {
    return campaignObjective || "selected campaign objective";
  }, [campaignObjective]);

  const selectedTouchpointsTemplates = useMemo(() => {
    const selectedTemplateValues = watchedTemplateValues || [];
    return selectedTemplateValues
      .map((templateId: string) => {
        return campaignTemplates.find((template) => template.id === templateId)
          ?.touchpointTemplates;
      })
      .filter((templates: MondayTouchpointTemplateDTO[] | undefined) => templates !== undefined)
      .flat() as MondayTouchpointTemplateDTO[];
  }, [watchedTemplateValues, campaignTemplates]);

  const renderTemplates = useCallback(() => {
    return selectedTouchpointsTemplates.map((template) => (
      <TouchpointTemplateItem
        key={template.id}
        template={template}
        invalidTemplateIds={invalidTemplateIds}
        existingTouchpointNames={existingTouchpointNames}
        campaignObjectiveDescription={campaignObjectiveDescription}
      />
    ));
  }, [
    selectedTouchpointsTemplates,
    invalidTemplateIds,
    existingTouchpointNames,
    campaignObjectiveDescription,
  ]);

  return (
    <div className="flex flex-col">
      {!isDirectToStep2 && (
        <p className="mb-4">Please edit campaign details as needed and proceed.</p>
      )}
      {isDirectToStep2 ? (
        <p className="mb-4">Select creative templates</p>
      ) : (
        <p className="mb-4">Selected creative templates</p>
      )}

      {isLoadingCampaignTemplates ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Icons.Loader customClass="animate-spin size-12 mb-4" />
          <p className="text-gray-600">Loading campaign template...</p>
        </div>
      ) : (
        <Box sx={TEMPLATE_LIST_SX}>{renderTemplates()}</Box>
      )}
    </div>
  );
};

/**
 * The submit button sits in the dialog's actions slot, outside the `<form>`, and joins it through the
 * `form` attribute. From a folder (`isDirectToStep2`) this is the only step, so there is no Back and
 * the pair keeps its old left-aligned order.
 */
export const TouchpointSelectionStepActions = ({ formId }: { formId: string }) => {
  const { back, setIsOpen, isDirectToStep2, isLoadingCampaignTemplates } =
    useAddTouchpointsFromMondayTemplateContext();
  const { watch, formState } = useFormContext();

  const templates = watch("templates");
  const hasSelectedTemplates = Object.values(templates || {}).some((value) => value === true);

  return (
    <DialogActions
      type={isDirectToStep2 ? "generic" : "steps"}
      back={
        isDirectToStep2 ? undefined : (
          <DialogBackButton onClick={back}>Back to templates</DialogBackButton>
        )
      }
      secondary={
        isDirectToStep2 ? (
          <Button size="large" variant="outlined" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
        ) : undefined
      }
      primary={
        <LoadingButton
          size="large"
          type="submit"
          form={formId}
          disabled={!hasSelectedTemplates || isLoadingCampaignTemplates}
          state={formState.isSubmitting ? "loading" : "default"}
          loadingMessage="Creating from templates...">
          Create from templates
        </LoadingButton>
      }
    />
  );
};

export default TouchpointSelectionStep;
